import React, { useState, useEffect } from 'react';

const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    const handleOpenContact = () => {
      setIsContactOpen(true);
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('openContactModal', handleOpenContact);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('openContactModal', handleOpenContact);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const menuItems = ['Our Story', 'Details', 'Gallery', 'Travel', 'FAQ', 'RSVP'];

  return (
    <>
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg py-4' : 'bg-transparent py-6'
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center">
            <button
              onClick={() => scrollToSection('hero')}
              className={`font-serif text-2xl ${
                isScrolled ? 'text-wedding-navy' : 'text-white'
              }`}
            >
              N & J
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {menuItems.map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                  className={`text-base font-semibold uppercase tracking-wider hover:text-wedding-gold transition-colors ${
                    isScrolled ? 'text-wedding-navy' : 'text-white drop-shadow-lg'
                  }`}
                >
                  {item}
                </button>
              ))}
              <button
                onClick={() => setIsContactOpen(true)}
                className="bg-wedding-gold text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-all"
              >
                Contact Us
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden ${isScrolled ? 'text-wedding-navy' : 'text-white'}`}
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4">
              <div className="flex flex-col space-y-4">
                {menuItems.map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                    className={`text-left text-base font-semibold uppercase tracking-wider hover:text-wedding-gold transition-colors ${
                      isScrolled ? 'text-wedding-navy' : 'text-white'
                    }`}
                  >
                    {item}
                  </button>
                ))}
                <button
                  onClick={() => {
                    setIsContactOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-left bg-wedding-gold text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-all w-fit"
                >
                  Contact Us
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Contact Modal */}
      {isContactOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
          onClick={() => setIsContactOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsContactOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h3 className="font-serif text-2xl text-wedding-navy mb-6 text-center">Contact Us</h3>

            <div className="space-y-6">
              {/* Nelisa */}
              <div className="bg-wedding-cream p-4 rounded-lg">
                <p className="font-semibold text-wedding-navy text-lg mb-2">Nelisa Oliveira</p>
                <div className="space-y-2">
                  <a
                    href="mailto:oliveiranelisa@gmail.com"
                    className="flex items-center text-gray-700 hover:text-wedding-gold transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2 text-wedding-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    oliveiranelisa@gmail.com
                  </a>
                  <a
                    href="tel:+15083103482"
                    className="flex items-center text-gray-700 hover:text-wedding-gold transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2 text-wedding-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    (508) 310-3482
                  </a>
                </div>
              </div>

              {/* Jesse */}
              <div className="bg-wedding-cream p-4 rounded-lg">
                <p className="font-semibold text-wedding-navy text-lg mb-2">Jesse Rosa</p>
                <div className="space-y-2">
                  <a
                    href="mailto:jessejrosa@gmail.com"
                    className="flex items-center text-gray-700 hover:text-wedding-gold transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2 text-wedding-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    jessejrosa@gmail.com
                  </a>
                  <a
                    href="tel:+17817903823"
                    className="flex items-center text-gray-700 hover:text-wedding-gold transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2 text-wedding-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    (781) 790-3823
                  </a>
                </div>
              </div>
            </div>

            <p className="text-center text-gray-500 text-sm mt-6">
              We'd love to hear from you!
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
